<div class="planning-board p-3">
  @if (config()) {
    @if (unavailableLabels().length) {
      <div class="alert alert-warning d-flex align-items-center gap-2" role="alert">
        <i class="digi-out-alert-info"></i>
        <span>
          {{ 'Doc::Planning:DataSourceUnavailable' | abpLocalization }} —
          {{ unavailableLabels().join(', ') }}
        </span>
      </div>
    }

    <!-- Line 1: planning title (left) — search + card-config (right).
         Reuses the dw-grid-list-page header visual: teal `text-primary` title and the
         standard pill quick-search (input-group-md + digi-out-search addon). -->
    <div class="planning-board__header">
      <h1 class="text-primary fs-5 mb-0">{{ planningTitle() }}</h1>

      <div class="planning-board__header-actions">
        @if (weekendToggleVisible()) {
          <div class="form-check form-switch planning-board__toggle">
            <input
              class="form-check-input"
              type="checkbox"
              role="switch"
              id="planning-show-weekends"
              [checked]="showWeekends()"
              (change)="showWeekends.set($any($event.target).checked)"
            />
            <label class="form-check-label" for="planning-show-weekends">{{
              'Doc::Planning:ShowWeekends' | abpLocalization
            }}</label>
          </div>
        }

        <div class="form-check form-switch planning-board__toggle">
          <input
            class="form-check-input"
            type="checkbox"
            role="switch"
            id="planning-fit-to-screen"
            [checked]="fitToScreen()"
            (change)="fitToScreen.set($any($event.target).checked)"
          />
          <label class="form-check-label" for="planning-fit-to-screen">{{
            'Doc::Planning:FitToScreen' | abpLocalization
          }}</label>
        </div>

        <div class="input-group input-group-md">
          <i class="digi-out-search input-group-text"></i>
          <input
            type="search"
            class="form-control"
            [placeholder]="'Core::Search' | abpLocalization"
            [value]="search()"
            (input)="search.set($any($event.target).value)"
          />
        </div>

        @if (configurableSources().length) {
          <button
            type="button"
            class="btn btn-sm btn-ghost-primary"
            [title]="'Doc::Planning:ConfigureCardFields' | abpLocalization"
            (click)="openCardConfig(cardConfigPanel)"
          >
            <i class="digi-out-settings"></i>
          </button>
        }
      </div>
    </div>

    <!-- Line 2: date navigation + range scale (left) — filter criteria (right). -->
    <core-kanban-toolbar>
      <core-toggle-select
        toolbarStart
        [options]="rangeOptions()"
        [value]="range()"
        (valueChange)="range.set($event)"
      />

      <core-kanban-week-nav
        toolbarStart
        [label]="rangeLabel()"
        [todayLabel]="'Doc::Planning:Today' | abpLocalization"
        [todayDisabled]="todayInView()"
        (previous)="previous()"
        (today)="today()"
        (next)="next()"
      />

      @for (source of filterSources(); track source.dataSource) {
        <div toolbarEnd class="planning-board__filter">
          @if (filterSources().length > 1) {
            <span class="planning-board__filter-label">{{
              dataSourceLabel(source.dataSource)
            }}</span>
          }
          <ng-container
            [documentPlanningFilterHost]="source.adapter.boardFilterComponent!"
            [filterSettings]="filterSettingsFor(source.dataSource)"
            [items]="itemsFor(source.dataSource)"
            [state]="initialFilterStateFor(source.dataSource)"
            (filterChange)="onFilterChange(source.dataSource, $event)"
            (stateChange)="onFilterStateChange(source.dataSource, $event)"
          />
        </div>
      }
    </core-kanban-toolbar>
  }

  <ng-template #cardConfigPanel>
    <document-planning-card-config-panel [sources]="configurableSources()" />
  </ng-template>

  <core-kanban-board
    [loading]="loading()"
    [fitToScreen]="fitToScreen()"
    [columns]="columns()"
    [groups]="kanbanGroups()"
    [items]="kanbanItems()"
    [allowDirectItems]="allowDirectItems()"
    [showNewGroupZone]="showNewGroupZone()"
    [canDrop]="canDrop"
    [dimItem]="dimItem()"
    [itemMenuItems]="itemMenuItems"
    [groupMenuItems]="groupMenuItems"
    [backlogTitle]="'Doc::Planning:Backlog' | abpLocalization"
    [newGroupLabel]="'Doc::Planning:NewGroup' | abpLocalization"
    [newGroupShortLabel]="'Doc::Planning:NewGroupShort' | abpLocalization"
    (itemDrop)="onItemDrop($event)"
    (groupDrop)="onGroupDrop($event)"
    (createGroup)="onCreateGroup($event)"
    (groupDelete)="onGroupDelete($event)"
    (itemDblClick)="onItemOpen($event)"
    (itemStatusChange)="onItemStatusChange($event)"
  >
    <ng-template kanbanItem let-data>
      @if (cardComponentFor(data.dataSource); as card) {
        <ng-container *ngComponentOutlet="card; inputs: cardInputsFor(data)" />
      }
    </ng-template>

    <ng-template kanbanGroupHeader let-data>
      <document-planning-free-group-card [group]="data.dto" />
    </ng-template>

    <ng-template kanbanBacklogFooter>
      <div class="legend">
        <button
          type="button"
          class="legend__toggle p-0"
          [attr.aria-expanded]="!legendCollapsed()"
          (click)="legendCollapsed.set(!legendCollapsed())"
        >
          <i [class]="legendCollapsed() ? 'digi-out-down' : 'digi-out-up'"></i>
          {{ 'Doc::Planning:Legend' | abpLocalization }}
        </button>

        @if (!legendCollapsed()) {
          <div class="legend__body">
            @for (group of legendGroups(); track group.dataSourceLabel; let last = $last) {
              <div class="legend__group">
                @if (legendGroups().length > 1) {
                  <span class="legend__source">{{ group.dataSourceLabel }}</span>
                }
                <div class="legend__entries">
                  @for (entry of group.entries; track entry.label) {
                    <span class="legend__item">
                      <span class="legend__dot" [style.background]="entry.color"></span>
                      {{ entry.label }}
                    </span>
                  }
                </div>
              </div>
              @if (!last) {
                <hr class="legend__separator m-0" />
              }
            }
          </div>
        }
      </div>
    </ng-template>

    <ng-template kanbanBacklogListFooter>
      @if (hasMoreBacklog()) {
        <button type="button" class="btn btn-outline-primary btn-sm w-100" (click)="loadMore()">
          {{ 'Doc::Planning:LoadMore' | abpLocalization }}
        </button>
      }
    </ng-template>
  </core-kanban-board>
</div>
